import { getFeatureFlag, isBoolean, isValidUrl } from "@sinch/feature-flags";
import { z } from "zod";
import {
  ENGAGE_MFE_BASE_DOMAIN,
  MFE_CAT_URL,
} from "../config/legacy-frozen-config";
import { mfeCatConfigPromise } from "../config/loadShellEnv";
import { ENTRY } from "../helpers/getEnv";
import engageApps from "./apps.engage.json";
import mmAppsLocal from "./apps.mm.functionaltest.json";
import mmApps from "./apps.mm.json";

const engageAppKeys = Object.keys(engageApps) as [
  keyof typeof engageApps,
  ...(keyof typeof engageApps)[]
];
const mmAppKeys = Object.keys(mmApps) as [
  keyof typeof mmApps,
  ...(keyof typeof mmApps)[]
];

export const remoteNameZod = z.enum([...engageAppKeys, ...mmAppKeys]);
export type RemoteName = z.infer<typeof remoteNameZod>;
export type MfeConfig = Record<
  string,
  { url: string; flags?: string[] | undefined }
>;

export const MFE_OVERRIDE_KEY = "_mfe";
export const MFE_VARIANT_OVERRIDE_KEY = "_variant";

const getEngageUrls = () =>
  Object.entries(engageApps).reduce<MfeConfig>(
    (acc, [consumerName, remote]) => {
      const [remoteName, mfeDomainFrag] = remote.split("@");
      const remoteUrl = `https://${mfeDomainFrag}${ENGAGE_MFE_BASE_DOMAIN}`;
      if (remoteName && remoteUrl) {
        return { ...acc, [consumerName]: { url: remoteUrl } };
      }
      return acc;
    },
    {}
  );

const getMessageMediaUrls = () => {
  const baseConfig =
    ENTRY === "mm" && !location.hostname.includes("localhost")
      ? mmApps
      : mmAppsLocal;

  // Handles temporary mm lab deployments
  const hostName = window.location.hostname.includes(
    "mm.web.labengage.sinch.com"
  )
    ? "onesaas-stg-syd-hub.s3.ap-southeast-2.amazonaws.com"
    : window.location.hostname;

  return Object.entries(baseConfig).reduce<MfeConfig>(
    (acc, [consumerName, remote]) => {
      const [remoteName, mfeDomainFrag] = remote.split("@");

      const remoteUrl = mfeDomainFrag?.includes("localhost")
        ? `http://${mfeDomainFrag}`
        : `https://${hostName}/${mfeDomainFrag}`;
      if (remoteName && remoteUrl) {
        return { ...acc, [consumerName]: { url: remoteUrl } };
      }

      return acc;
    },
    {}
  );
};

export const getBaseMfeConfig = () => ({
  ...getEngageUrls(),
  ...getMessageMediaUrls(),
});

const mainBranchVariants = ["main", "prod", "master"];

/**
 * Promise (as a self-calling async function in this case) so that it is loaded
 * directly when the app is opened and loadMfe can await the fullfilled value
 */
const loadingMfeUrls = (async (): Promise<MfeConfig> => {
  try {
    const mfeCatConfig = await mfeCatConfigPromise;
    const { mfes } = mfeCatConfig;

    const variantOverrides = JSON.parse(
      sessionStorage.getItem(MFE_VARIANT_OVERRIDE_KEY) || "[]"
    ) as string[];
    const mfeUploadServiceUrls = mfes
      .filter(
        (mfe) =>
          /** exclude BotBuilder since that mfe doesn't use Mfecat */
          mfe.name !== "BotBuilderV2" &&
          (variantOverrides.includes(mfe.variant) ||
            mainBranchVariants.includes(mfe.variant))
      )
      // Make sure the overrides come last sor that they overwrite the "main"
      // in the forEach
      .sort((mfe) => (mainBranchVariants.includes(mfe.variant) ? -1 : 1))
      .reduce<MfeConfig>(
        (acc, mfe) => ({
          ...acc,
          [mfe.name]: {
            url: `${MFE_CAT_URL}/${mfe.revision}`,
            flags: mfe.flags,
          },
        }),
        {}
      );

    return mfeUploadServiceUrls;
  } catch (error) {
    console.error("Failed to load remotes from MFE Service", error);
    return {};
  }
})();

const addOverrides = (config: MfeConfig): MfeConfig => {
  const storedOverrides = sessionStorage.getItem(MFE_OVERRIDE_KEY);
  if (!storedOverrides) {
    return config;
  }

  const parsedOverrides = JSON.parse(storedOverrides) as string[];

  return parsedOverrides.reduce(
    (config, override) => {
      const [mfeInfo, overrideUrl] = override.split("@");

      if (mfeInfo && overrideUrl) {
        const [name, flagString] = mfeInfo.split(":");
        const flags = flagString?.split(",");
        if (name) {
          return {
            ...config,
            [name]: {
              ...config[name],
              ...(flags ? { flags } : {}),
              url: overrideUrl,
            },
          };
        }
      }
      return config;
    },
    { ...config }
  );
};

export const getMfeConfig = async () =>
  addOverrides({
    ...getBaseMfeConfig(),
    // Loading the first time, next time the Promise will be fullfilled
    ...(await loadingMfeUrls),
    /** Chatlayer doesn't have one artifact with dynamic environment, so the MFE needs to be loaded manually */
    BotBuilderV2: {
      url: getFeatureFlag(
        "mmBotBuilderMfeUrl",
        isValidUrl,
        "https://app.chatbots.messagemedia.com"
      ),
      flags: getFeatureFlag("mmBotBuilderMfeIsESM", isBoolean, false)
        ? ["esm"]
        : [],
    },
  });

getMfeConfig().then((config) =>
  console.log("Remote config", {
    ...config,
  })
);
